<template>
  <div
    v-show="visible"
    v-loading="loading"
    class="panel-screen-renderer">
    <div
      v-if="nestBlockMessage"
      class="panel-screen-renderer__nest-block">
      {{ nestBlockMessage }}
    </div>
    <div
      v-if="screenConfig"
      class="scale-wrap"
      :style="wrapStyle">
      <div
        class="scaled-inner"
        :style="scaledInnerStyle">
        <div
          v-for="(item,index) in screenConfig.chartViews"
          v-show="showComponentInView(item)"
          :key="'psub-' + panelSn + '-' + (item.addSn != null ? item.addSn : 'n') + '-' + index"
          :style="{
            width: pxNum(item.chartWidth),
            height: pxNum(item.chartHeight),
            zIndex: item.sort,
            transform: `translate(${pxNum(item.chartX)},${pxNum(item.chartY)})`
          }"
          class="screen-component-item">
          <component
            :is="resolveChartViewIs(item)"
            :ref="'chartRef' + index"
            :key="'psub-' + panelSn + '-' + item.addSn + '-' + index"
            :config="item"
            :component-type="item.chartComponent"
            :global-config="screenConfig"
            :filter-params="filterParams"
            :tab-scope-id="effectiveTabScopeId"
            :composite-nest-level="compositeNestLevelForChild(item)"
            :is-disabled="item.chartComponentType === 'FilterBlock' && !!item.chartSet?.chartStyle?.readOnlyInView"
            component-status="view"
            @click.native="handleJump(item)"
            @filterChange="updateComponentData" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import TextBlock from '@/components/charts/text.vue'
import TimeClock from '@/components/charts/timeClock.vue'
import MapChart from '@/components/charts/mapChart.vue'
import PieChart from '@/components/charts/pieChart.vue'
import LineChart from '@/components/charts/lineChart.vue'
import LineBarMixChart from '@/components/charts/lineBarMixChart.vue'
import BarChart from '@/components/charts/barChart.vue'
import NumberChart from '@/components/charts/number/number.vue'
import IndependentNumber from '@/components/charts/number/independentNumber.vue'
import Gauge from '@/components/charts/number/gauge.vue'
import ScrollTable from '@/components/charts/table/scrollTable.vue'
import Rank from '@/components/charts/rank.vue'
import RichTextBlock from '@/components/charts/material/showRich.vue'
import BorderBlock from '@/components/charts/material/borderBlock.vue'
import FilterBlock from '@/components/charts/material/filterBlock.vue'
import HeatMapChart from '@/components/charts/heatMap.vue'
import SignName from '@/components/charts/material/signName.vue'
import ImageBlock from '@/components/charts/image.vue'
import CompositeTabBar from '@/components/charts/compositeTabBar.vue'
import { getScreenDataApi } from '@/views/screen/fetch'
import { defaultFilterDate } from '@/views/screen/data'
import { orderFilterDateRange } from '@/utils/filterDateRange'
import { hydrateScreenChartViews } from '@/views/screen/hydrateScreenChartViews'
import {
  alignCompositeShellPair,
  COMPOSITE_NEST_RUNTIME_BLOCK_MESSAGE,
  chartViewItemIsCompositePanelHost,
  isCompositeScreenForNestCap,
} from '@/utils/compositeScreen'
import { randomString } from '@/utils/common'

/** 与 defaultComponentConfig / 接口一致的类型名 → 本组件内注册的 PascalCase 名 */
const CHART_COMPONENT_TYPE_ALIAS = {
  imageBlock: 'ImageBlock',
  compositeTabBar: 'CompositeTabBar',
  compositePanelHost: 'CompositePanelHost',
}
const CHART_COMPONENT_FORCE_MAP = {
  text: 'TextBlock',
  timeClock: 'TimeClock',
  mapChart: 'MapChart',
  pie: 'PieChart',
  pieCircular: 'PieChart',
  line: 'LineChart',
  areaLine: 'LineChart',
  lineBarMix: 'LineBarMixChart',
  verticalBar: 'BarChart',
  horizonBar: 'BarChart',
  barPolar: 'BarChart',
  number: 'Number',
  independentNumber: 'IndependentNumber',
  gaugeNumber: 'Gauge',
  scrollTable: 'ScrollTable',
  rank: 'Rank',
  richText: 'RichTextBlock',
  borderBlock: 'BorderBlock',
  filterBlock: 'FilterBlock',
  heatMapChart: 'HeatMapChart',
  signName: 'SignName',
  imageBlock: 'ImageBlock',
  compositeTabBar: 'CompositeTabBar',
  compositePanelHost: 'CompositePanelHost',
}

function coercePositiveNumber (v, fallback) {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export default {
  name: 'PanelScreenRenderer',
  components: {
    TextBlock,
    TimeClock,
    MapChart,
    PieChart,
    LineChart,
    LineBarMixChart,
    BarChart,
    Number: NumberChart,
    IndependentNumber,
    Gauge,
    ScrollTable,
    Rank,
    RichTextBlock,
    BorderBlock,
    FilterBlock,
    HeatMapChart,
    SignName,
    ImageBlock,
    CompositeTabBar,
    // 与 compositePanelHost.vue 存在双向引用；改为异步组件打断冷启动时的循环依赖
    CompositePanelHost: () => import('@/components/charts/compositePanelHost.vue'),
  },
  props: {
    /** 接口 id 可能为数字；统一在 load 里转成字符串请求 */
    panelSn: {
      type: [String, Number],
      required: true,
    },
    /**
     * 嵌套组合大屏：TabBar / Host 必须共享同一 scope，否则激活 tab 写在 A scope、Host 读 B scope，会导致子屏永不加载
     * 由外层传入；最外层不传则内部自动生成
     */
    tabScopeId: {
      type: [String, Number],
      default: '',
    },
    visible: {
      type: Boolean,
      default: true,
    },
    width: {
      type: [Number, String],
      default: 1920,
    },
    height: {
      type: [Number, String],
      default: 1080,
    },
    /**
     * 组合嵌套深度：根组合下第一层子屏为 0；再进一层子屏容器 +1。
     * ≥1 时禁止再加载组合大屏（与保存「仅一层嵌套」一致）。
     */
    compositeNestLevel: {
      type: Number,
      default: 0,
    },
  },
  data () {
    return {
      screenConfig: null,
      loading: false,
      nestBlockMessage: '',
      nestLimitToastSn: '',
      /** 最外层 renderer 自动生成 scope；嵌套 renderer 优先使用 props.tabScopeId */
      localTabScopeId: `psr_${randomString()}_${randomString()}`,
      filterParams: {
        startTime: defaultFilterDate.startTime,
        endTime: defaultFilterDate.endTime,
      },
      skipMapStyleSyncFromFilter: false,
      loadSerial: 0,
      /** 组合大屏子屏缓存：仅在可见时才首次拉数，避免 v-show 常驻导致一次性并发加载所有子屏 */
      hasLoadedOnce: false,
    }
  },
  computed: {
    effectiveTabScopeId () {
      const p = this.tabScopeId
      if (p != null && String(p).trim() !== '') return String(p)
      return this.localTabScopeId
    },
    wrapStyle () {
      const w = coercePositiveNumber(this.width, 1920)
      const h = coercePositiveNumber(this.height, 1080)
      return {
        width: w + 'px',
        height: h + 'px',
        overflow: 'hidden',
        position: 'relative',
      }
    },
    scaledInnerStyle () {
      if (!this.screenConfig) return {}
      const pw = this.screenConfig.panelWidth || 1920
      const ph = this.screenConfig.panelHeight || 1080
      const boxW = coercePositiveNumber(this.width, 1920)
      const boxH = coercePositiveNumber(this.height, 1080)
      const sx = boxW / pw
      const sy = boxH / ph
      return {
        width: pw + 'px',
        height: ph + 'px',
        transform: `scale(${sx},${sy})`,
        transformOrigin: '0 0',
        position: 'absolute',
        left: 0,
        top: 0,
        background: this.screenConfig.backgroundType === 1
          ? this.screenConfig.background
          : `url(${this.screenConfig.background}) no-repeat 100% 100%/100% 100%`,
      }
    },
  },
  watch: {
    panelSn: {
      immediate: true,
      handler (val, oldVal) {
        if (oldVal !== undefined && String(val ?? '') !== String(oldVal ?? '')) {
          this.hasLoadedOnce = false
        }
        this.maybeLoad()
      },
    },
    visible: {
      immediate: true,
      handler () {
        this.maybeLoad()
      },
    },
  },
  created () {
    this._coalescedUpdatePayload = null
    this._debouncedUpdateComponentData = _.debounce(() => {
      const fp = this._coalescedUpdatePayload
      this._coalescedUpdatePayload = null
      if (fp && typeof fp === 'object' && Object.keys(fp).length) {
        this._runUpdateComponentData(fp)
      }
    }, 60)
  },
  beforeDestroy () {
    const d = this._debouncedUpdateComponentData
    if (d && typeof d.cancel === 'function') d.cancel()
    this._coalescedUpdatePayload = null
  },
  methods: {
    maybeLoad () {
      const sn = this.panelSn == null || this.panelSn === '' ? '' : String(this.panelSn).trim()
      if (!sn) return
      // 不可见时不拉数；等切到可见再加载，切换后不销毁则自然缓存
      if (!this.visible) return
      // 已加载过且 panelSn 未变更时不重复拉，保留用户修改的 filterParams
      if (this.hasLoadedOnce) return
      this.hasLoadedOnce = true
      this.load()
    },
    pxNum (v) {
      const n = Number(v)
      return `${Number.isFinite(n) ? n : 0}px`
    },
    /** chartComponentType 被接口污染成对象时，:is 会报 “[object Object] is not a constructor” */
    resolveChartViewIs (item) {
      const normalizeKey = (v) => {
        if (v == null) return ''
        return String(v).trim().replace(/\s+/g, '')
      }
      const normalizeLower = (v) => normalizeKey(v).toLowerCase()
      // 组合壳组件走硬匹配，避免刷新后偶发字段污染导致回退到 TextBlock
      if (chartViewItemIsCompositePanelHost(item)) {
        return this.$options.components.CompositePanelHost
      }
      {
        const c = normalizeLower(item && item.chartComponent)
        const t = normalizeLower(item && item.chartComponentType)
        if (c === 'compositetabbar' || t === 'compositetabbar') {
          return this.$options.components.CompositeTabBar
        }
      }
      const rawType = item && item.chartComponentType
      const rawComponent = item && item.chartComponent
      const componentEntries = Object.keys(this.$options.components || {})
      const componentKeyByLower = componentEntries.reduce((acc, k) => {
        acc[k.toLowerCase()] = k
        return acc
      }, {})
      const resolveRegisteredName = (name) => {
        const n = normalizeKey(name)
        if (!n) return ''
        if (this.$options.components[n]) return n
        const byLower = componentKeyByLower[n.toLowerCase()]
        return byLower || ''
      }
      const toComponentDef = (name) => {
        const key = resolveRegisteredName(name)
        return key ? this.$options.components[key] : null
      }
      // 先按 chartComponent 强制映射，避免 chartComponentType 被污染时误回退到 TextBlock
      const force = CHART_COMPONENT_FORCE_MAP[normalizeLower(rawComponent)]
      const forceResolved = toComponentDef(force)
      if (forceResolved) return forceResolved
      const candidates = []
      if (typeof rawType === 'string' && normalizeKey(rawType)) {
        const t = normalizeKey(rawType)
        candidates.push(t)
        const alias = CHART_COMPONENT_TYPE_ALIAS[t] || CHART_COMPONENT_TYPE_ALIAS[normalizeLower(t)]
        if (alias) candidates.push(alias)
      } else if (rawType && typeof rawType === 'object') {
        const t = normalizeKey(rawType.name || rawType.componentName || rawType.type)
        if (t) {
          candidates.push(t)
          const alias = CHART_COMPONENT_TYPE_ALIAS[t] || CHART_COMPONENT_TYPE_ALIAS[normalizeLower(t)]
          if (alias) candidates.push(alias)
        }
      }
      if (typeof rawComponent === 'string' && normalizeKey(rawComponent)) {
        const c = normalizeKey(rawComponent)
        candidates.push(c)
        const alias = CHART_COMPONENT_TYPE_ALIAS[c] || CHART_COMPONENT_TYPE_ALIAS[normalizeLower(c)]
        if (alias) candidates.push(alias)
      }
      for (const key of candidates) {
        const resolved = toComponentDef(key)
        if (resolved) return resolved
      }
      if (process.env.NODE_ENV !== 'production') {
        // 调试偶发回退：只在开发态输出
        // eslint-disable-next-line no-console
        console.warn('[PanelScreenRenderer] resolveChartViewIs fallback TextBlock', {
          chartComponent: rawComponent,
          chartComponentType: rawType,
          chartTitle: item && item.chartTitle,
          addSn: item && item.addSn,
        })
      }
      return this.$options.components.TextBlock
    },
    showComponentInView (item) {
      if (item.chartComponent !== 'filterBlock') return true
      return !item.chartSet?.chartStyle?.hideInView
    },
    handleJump (item) {
      if (item.jumpPanelSn) {
        this.$router.push({ path: `/screen/view/${item.jumpPanelSn}` })
      }
    },
    compositeNestLevelForChild (item) {
      if (!chartViewItemIsCompositePanelHost(item)) return undefined
      const base = Number(this.compositeNestLevel)
      const depth = Number.isFinite(base) && base >= 0 ? base : 0
      return depth + 1
    },
    async load () {
      const sn = this.panelSn == null || this.panelSn === '' ? '' : String(this.panelSn).trim()
      if (!sn) return
      const gen = ++this.loadSerial
      this.loading = true
      this.nestBlockMessage = ''
      try {
        const res = await getScreenDataApi(sn)
        if (gen !== this.loadSerial) return
        hydrateScreenChartViews(res)
        // 组合大屏：确保 Tab 与子屏容器 groupId 配对，避免嵌套渲染时找错 partnerTabs
        alignCompositeShellPair(res.chartViews)
        const nestDepthRaw = Number(this.compositeNestLevel)
        const nestDepth =
          Number.isFinite(nestDepthRaw) && nestDepthRaw >= 0 ? nestDepthRaw : 0
        if (isCompositeScreenForNestCap(res) && nestDepth >= 1) {
          this.screenConfig = null
          this.nestBlockMessage = COMPOSITE_NEST_RUNTIME_BLOCK_MESSAGE
          if (this.nestLimitToastSn !== sn) {
            this.nestLimitToastSn = sn
            this.$message && this.$message.warning(this.nestBlockMessage)
          }
          return
        }
        this.nestLimitToastSn = ''
        if (res.backgroundType === 1) {
          res.background = res.backgroundColor
        } else {
          res.background = res.backgroundTemplate
        }
        this.screenConfig = res
        const fp0 = this.filterParams || {}
        const st0 = fp0.startTime || defaultFilterDate.startTime
        const et0 = fp0.endTime || defaultFilterDate.endTime
        const ord = orderFilterDateRange(st0, et0)
        this.filterParams = {
          startTime: ord.startTime || defaultFilterDate.startTime,
          endTime: ord.endTime || defaultFilterDate.endTime,
          panelSn: sn,
        }
        if (res.filterParams) {
          try {
            const parsed = JSON.parse(res.filterParams)
            const { startTime, endTime, ...rest } = {
              ...this.filterParams,
              ...parsed,
              panelSn: sn,
            }
            const o = orderFilterDateRange(
              startTime || defaultFilterDate.startTime,
              endTime || defaultFilterDate.endTime,
            )
            this.filterParams = {
              ...rest,
              panelSn: sn,
              startTime: o.startTime || defaultFilterDate.startTime,
              endTime: o.endTime || defaultFilterDate.endTime,
            }
          } catch (e) {
            this.filterParams = { ...this.filterParams, panelSn: sn }
          }
        } else {
          this.filterParams = { ...this.filterParams, panelSn: sn }
        }
        await this.$nextTick()
        if (gen !== this.loadSerial) return
        this.skipMapStyleSyncFromFilter = true
        this.updateComponentData(this.filterParams)
      } catch (e) {
        this.$error(e)
        this.screenConfig = null
        this.nestBlockMessage = ''
      } finally {
        this.loading = false
      }
    },
    updateComponentData (filterParams) {
      if (filterParams != null && typeof filterParams === 'object') {
        this._coalescedUpdatePayload = {
          ...(this._coalescedUpdatePayload ?? {}),
          ...filterParams,
        }
        this._debouncedUpdateComponentData()
        return
      }
      this._runUpdateComponentData(undefined)
    },
    _runUpdateComponentData (filterParams) {
      const skipMapStyleSyncThisRun = this.skipMapStyleSyncFromFilter
      if (this.skipMapStyleSyncFromFilter) {
        this.skipMapStyleSyncFromFilter = false
      }
      this.filterParams.panelSn = this.panelSn == null || this.panelSn === '' ? '' : String(this.panelSn).trim()
      const keys = filterParams ? Object.keys(filterParams) : []
      const incomingTouchesRegion =
        filterParams &&
        typeof filterParams === 'object' &&
        (Object.prototype.hasOwnProperty.call(filterParams, 'provinceCode') ||
          Object.prototype.hasOwnProperty.call(filterParams, 'cityCode') ||
          Object.prototype.hasOwnProperty.call(filterParams, 'regionCode'))
      if (filterParams) {
        const { startTime, endTime, ...rest } = { ...(this.filterParams ?? {}), ...filterParams }
        const st = startTime || defaultFilterDate.startTime
        const et = endTime || defaultFilterDate.endTime
        const o = orderFilterDateRange(st, et)
        this.filterParams = {
          ...rest,
          startTime: o.startTime || defaultFilterDate.startTime,
          endTime: o.endTime || defaultFilterDate.endTime,
        }
      }
      const n = this.screenConfig && this.screenConfig.chartViews ? this.screenConfig.chartViews.length : 0
      for (let index = 0; index < n; index++) {
        const key = 'chartRef' + index
        const ref = this.$refs[key]
        const component = Array.isArray(ref) ? ref[0] : ref
        if (
          incomingTouchesRegion &&
          !skipMapStyleSyncThisRun &&
          component?.config?.chartComponent === 'mapChart'
        ) {
          const { provinceCode, cityCode, regionCode } = this.filterParams
          if (provinceCode || cityCode || regionCode) {
            const mapData = regionCode ? ['100000', provinceCode, cityCode, regionCode] : (cityCode ? ['100000', provinceCode, cityCode] : ['100000', provinceCode])
            component.config.chartSet.chartStyle.map = mapData
          }
        }
        if (
          component?.getChartData &&
          typeof component.getChartData === 'function' &&
          component?.config?.chartComponent !== 'filterBlock'
        ) {
          component.getChartData(this.filterParams)
        }
        if (component && component.config && component.config.chartComponent === 'filterBlock' && filterParams && filterParams.addSn && !keys.includes('startTime')) {
          if (component.config.addSn !== filterParams.addSn) {
            component.clearDate()
          }
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.panel-screen-renderer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
.panel-screen-renderer__nest-block {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.35);
  z-index: 2;
  box-sizing: border-box;
}
.scale-wrap {
  box-sizing: border-box;
}
.scaled-inner {
  .screen-component-item {
    position: absolute;
    box-sizing: border-box;
  }
}
</style>
